import { Component, Show, onMount } from "solid-js";
import _ from "underscore";
import { A, useNavigate, useRouteData } from "@solidjs/router";
import { commitChange, fetchPost, scrollToTop } from "../library/common";
import ImageCropper, { IImageCropperWrapper,  ImageFileData } from "../components/universal/image-cropper";
import { ArtworkImageData, KeyOfType } from "../types";
import { Progress } from "../components/progress";
import { createStore } from "solid-js/store";
import { PrepareImageText } from "../components/prepare-image-text";
import toast from "solid-toast";
import { CMS } from "../components/cms/cms";
import { loadCms } from "../app";
import { PageLoading } from "../components/page-loading";
import { Button } from "solid-bootstrap";

type State = {
    isContentReady: boolean;
}
type ButtonActiveState = {
    submit: boolean;
    cancel: boolean;
}

export function EditImage() {  
    const navigate = useNavigate();
    const [state, setState] = createStore<State>({ isContentReady: false });
    const [data, setData] = createStore<ArtworkImageData>({} as any);
    const [buttonActive, setButtonActive] = createStore<ButtonActiveState>({ submit: true, cancel: true } as ButtonActiveState);
    const enableButtons = () => setButtonActive({ submit: true, cancel: true });
    const disableButtons = () => setButtonActive({ submit: false, cancel: false });
    const toSalonEntrantImageData = (salonSubmissionBaseId: string, imageFile: ImageFileData) => {
        var extension = imageFile.filename?.split(".")?.at(-1);
        let model = {
            IsPrimary: data.isPrimary,
            SalonSubmissionBaseId: salonSubmissionBaseId || "",
            NewImageId: data.newImage.id || "",
            Image: imageFile.image,
            OriginalFilename: imageFile.filename,
            FilenameExtension: extension || "jpg"
        };
        return model;
    };
    const ModelImageCropper : Component<{ imageUrl: keyof KeyOfType<ArtworkImageData, string>; required?: boolean |(() => boolean); editor: keyof KeyOfType<ArtworkImageData, IImageCropperWrapper>; label?: string; }> = (props) =>
    <>
        <ImageCropper src={data[props.imageUrl]} required={props.required} maxHeight={6000} maxWidth={6000} setInstance={(wrapper) => setData(props.editor, wrapper)} />
    </>;
    const saveImage = async (imageFile: ImageFileData) => {
        const msgId = toast.loading(`Saving image ...`);
        try {
            const model = toSalonEntrantImageData(data.artwork.id, imageFile);
            const response = await fetchPost("/SalonApplication/SalonSubmission/SaveImageStreamingJson", model);
            const json = await response.json();
            if (!json.Success) {
                await commitError("There was an error uploading the image.");
                return toast.error("There was an error uploading the image.", { id: msgId });
            }
            toast.success(`The image has been saved.`, { id: msgId });
            navigate("/step-2");
        } catch (ex) {
            await commitError("There was an exception uploading the image.", ex);
            toast.error("There was an error uploading the image.", { id: msgId });
            return;
        } finally {
            enableButtons();
        }
    }
    const commitError = async (error: string, ex?: unknown) => {
        await commitChange("18th-arc-salon-application/edit-image/error", {
            SalonSubmissionBaseImageId: data.id,
            SalonSubmissionBaseId: data.artwork?.id,
            Error: error,
            Exception: ex?.toString()
        });
        toast.error(error);
    }
    const onSaveClicked = async (e: Event) => {
        e.preventDefault();
        disableButtons();
        if (data.artwork.id === undefined) {
            return await commitError("Artwork data was not retrieved. Please refresh the page.");
        }
        const imageFile = await data.imageEditor?.getImageFile();
        if (imageFile == null) {
            return await commitError("There is no image selected to upload.");
        }
        await commitChange("18th-salon-application/edit-image", {
            SalonSubmissionBaseImageId: data.id,
            SalonSubmissionBaseId: data.artwork?.id,
            OriginalFilename: imageFile.filename,
            FileSize: imageFile?.image?.size,
            Errors: imageFile?.errors,
            IsValid: imageFile?.isValid,
            IsDirty: imageFile?.isDirty
        });
        await saveImage(imageFile);
    }
    onMount(async () => {
        const routeData = await useRouteData() as ArtworkImageData;
        console.log("onMount", { routeData });
        setData(routeData);
        if (!!routeData.newImage?.url) {
            setData("imageUrl", routeData.newImage.url);
        }
        await loadCms([
            "button::cancel", "button::save"
        ]);
        setState({ isContentReady: true });
        scrollToTop();
    });
    return (
        <>
            <div>
                <Progress step={2}></Progress>
                <div class="row ">
                    <div class="col-md-12">
                        <PrepareImageText />
                    </div>
                </div>
                <Show when={state.isContentReady} fallback={<PageLoading />}>
                    <div class="row">
                        <div class="col-md-12">
                            <div class="row" style="border: 1px solid #ccc; padding: 30px; margin: 0px; margin-top: 25px; border-radius: 10px;">
                                <div class="col-md-12">
                                    <div>
                                        <label style="font-size: 1.5em;">
                                            <CMS id="label::image">Image</CMS>
                                        </label>
                                        <ModelImageCropper imageUrl="imageUrl" editor="imageEditor" required />
                                    </div>                                
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <Button  href={"#/step-2"} variant="secondary" classList={{ ["disabled"] : !buttonActive.cancel }} style="margin-right: 20px;">
                                <CMS text id="button::cancel">Cancel</CMS>
                            </Button>
                            <Button variant="success" onClick={onSaveClicked} classList={{ ["disabled"] : !buttonActive.submit }}>
                                <CMS text id="button::save">Save</CMS>
                            </Button>
                        </div>
                    </div>
                </Show>
            </div>
        </>
    );
}